import { Star, Car, Globe, Music, GraduationCap, Edit2, Heart } from 'lucide-react';
import { UserProfile, UserRole } from '@/types';

interface ProfileCardProps {
  user: UserProfile;
  isOwnProfile?: boolean;
  onEdit?: () => void;
  onMessage?: () => void; // Added for messaging functionality
  showMessageButton?: boolean; // Control visibility of message button
}

const ProfileCard = ({ 
  user, 
  isOwnProfile = false, 
  onEdit,
  onMessage,
  showMessageButton = false 
}: ProfileCardProps) => {
  
  // Type guard for user name to ensure string type
  const getInitials = (name: string): string => {
    if (!name || typeof name !== 'string') return '?';
    return name
      .split(' ')
      .map(n => n[0])
      .filter(Boolean) // Remove empty strings
      .join('')
      .toUpperCase()
      .slice(0, 2); // Limit to 2 characters
  };

  // Safe access to user properties with defaults
  const safeUser: Required<Pick<UserProfile, 'major' | 'languages' | 'favoriteMusic' | 'hobbies' | 'rating' | 'ridesCompleted'>> & 
    Pick<UserProfile, 'name' | 'role'> = {
    name: user?.name ?? 'Unknown User',
    role: (user?.role as UserRole) ?? 'rider',
    major: user?.major ?? 'Undeclared',
    languages: user?.languages ?? ['English'],
    favoriteMusic: user?.favoriteMusic ?? 'Various',
    hobbies: user?.hobbies ?? [],
    rating: user?.rating ?? 5.0,
    ridesCompleted: user?.ridesCompleted ?? 0,
  };

  // Determine role badge styles with proper typing
  const getRoleBadgeStyles = (role: UserRole): string => {
    if (role === 'driver') return 'bg-primary/10 text-primary';
    if (role === 'admin' || role === 'super_admin') return 'bg-amber-500/10 text-amber-600';
    return 'bg-accent/10 text-accent';
  };

  const getRoleLabel = (role: UserRole): string => {
    if (role === 'driver') return 'Driver';
    if (role === 'admin') return 'School Admin';
    if (role === 'super_admin') return 'Super Admin';
    return 'Rider';
  };

  return (
    <div className="bg-card rounded-2xl p-6 shadow-warm border border-border/50">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          {/* Avatar - show image if available, otherwise show initials */}
          {user?.avatar ? (
            <img 
              src={user.avatar} 
              alt={safeUser.name}
              className="w-16 h-16 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-16 h-16 rounded-full gradient-warm flex items-center justify-center flex-shrink-0">
              <span className="text-xl font-bold text-primary-foreground">
                {getInitials(safeUser.name)}
              </span>
            </div>
          )}
          <div className="min-w-0 flex-1"> {/* Added min-w-0 for text truncation */}
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-display font-bold text-xl text-foreground truncate">
                {safeUser.name}
              </h2>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeStyles(safeUser.role)}`}>
                {getRoleLabel(safeUser.role)}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <div className="flex items-center gap-1 text-amber-500">
                <Star className="w-4 h-4 fill-current" />
                <span className="font-semibold text-sm">{safeUser.rating.toFixed(1)}</span>
              </div>
              <span className="text-muted-foreground">•</span>
              <div className="flex items-center gap-1 text-muted-foreground text-sm">
                <Car className="w-4 h-4" />
                <span>{safeUser.ridesCompleted} rides</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {showMessageButton && onMessage && !isOwnProfile && (
            <button
              onClick={onMessage}
              className="p-2 rounded-xl bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
              aria-label="Send message"
            >
              <svg 
                className="w-5 h-5" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" 
                />
              </svg>
            </button>
          )}
          {isOwnProfile && (
            <button
              onClick={onEdit}
              className="p-2 rounded-xl bg-muted hover:bg-muted/80 transition-colors"
              aria-label="Edit profile"
            >
              <Edit2 className="w-5 h-5 text-muted-foreground" />
            </button>
          )}
        </div>
      </div>

      {/* Intro Section */}
      <div className="space-y-4">
        <h3 className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-wide">
          About Me
        </h3>

        <div className="space-y-3">
          {/* Major */}
          <div className="flex items-center gap-3 p-3 bg-muted rounded-xl">
            <div className="p-2 rounded-lg bg-secondary flex-shrink-0">
              <GraduationCap className="w-4 h-4 text-accent" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground">Major</p>
              <p className="font-medium text-foreground truncate">{safeUser.major}</p>
            </div>
          </div>

          {/* Languages */}
          <div className="flex items-center gap-3 p-3 bg-muted rounded-xl">
            <div className="p-2 rounded-lg bg-secondary flex-shrink-0">
              <Globe className="w-4 h-4 text-accent" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground">Languages</p>
              <p className="font-medium text-foreground truncate">
                {safeUser.languages.join(', ')}
              </p>
            </div>
          </div>

          {/* Favorite Music */}
          <div className="flex items-center gap-3 p-3 bg-muted rounded-xl">
            <div className="p-2 rounded-lg bg-secondary flex-shrink-0">
              <Music className="w-4 h-4 text-accent" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground">Favorite Music</p>
              <p className="font-medium text-foreground truncate">{safeUser.favoriteMusic}</p>
            </div>
          </div>

          {/* Hobbies */}
          {safeUser.hobbies.length > 0 && (
            <div className="flex items-start gap-3 p-3 bg-muted rounded-xl">
              <div className="p-2 rounded-lg bg-secondary flex-shrink-0">
                <Heart className="w-4 h-4 text-accent" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground">Hobbies</p>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {safeUser.hobbies.map((hobby, index) => (
                    <span
                      key={`${hobby}-${index}`} // Better key using hobby name
                      className="px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs font-medium"
                    >
                      {hobby}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileCard;
